'use client';

import React, { useEffect, useRef } from 'react';

export default function CtaSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const elements = sectionRef.current?.querySelectorAll('.reveal-on-scroll');
    if (!elements) return;
    elements.forEach((el) => el.classList.add('hidden-initially'));

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="cta"
      className="relative overflow-hidden grain-overlay"
      style={{
        backgroundColor: '#1C2B25',
        paddingTop: 112,
        paddingBottom: 112,
      }}
    >
      {/* Background atmospheric blobs */}
      <div
        className="blob-1 absolute pointer-events-none"
        style={{
          width: 600,
          height: 600,
          top: '-20%',
          left: '-10%',
          background: 'radial-gradient(circle, rgba(44,74,62,0.6) 0%, transparent 70%)',
          filter: 'blur(80px)',
          opacity: 0.5,
        }}
      />
      <div
        className="blob-2 absolute pointer-events-none"
        style={{
          width: 400,
          height: 400,
          bottom: '-15%',
          right: '-5%',
          background: 'radial-gradient(circle, rgba(139,105,20,0.3) 0%, transparent 70%)',
          filter: 'blur(60px)',
          opacity: 0.5,
        }}
      />
      {/* Grid lines */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)
          `,
          backgroundSize: '80px 80px',
        }}
      />

      <div className="max-w-[1280px] mx-auto px-6 lg:px-8 relative z-10">
        <div className="max-w-3xl mx-auto text-center">

          {/* Ornament */}
          <div className="reveal-on-scroll stagger-1 mb-8 flex justify-center">
            <span
              className="text-3xl float-slow"
              style={{ color: 'rgba(179,138,42,0.4)' }}
            >
              ✦
            </span>
          </div>

          <h2
            className="reveal-on-scroll stagger-2 font-heading text-white mb-6"
            style={{
              fontSize: 'clamp(2.4rem, 6vw, 5rem)',
              lineHeight: 1.0,
              letterSpacing: '-0.03em',
              fontWeight: 600,
            }}
          >
            An open toolkit.{' '}
            <span className="text-shimmer">
              Anywhere we can come to understand.
            </span>
          </h2>

          <p
            className="reveal-on-scroll stagger-3 font-body mb-4 max-w-xl mx-auto leading-relaxed"
            style={{ fontSize: '1.1rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.75 }}
          >
            Stand on it. Work from it. For us, by us,{' '}
            <span className="font-mono" style={{ color: '#B38A2A', fontFamily: 'var(--font-mono)' }}>fisabilillah</span>.
          </p>

          <p
            className="reveal-on-scroll stagger-3 font-heading mb-12 max-w-lg mx-auto"
            style={{
              fontSize: '1.3rem',
              color: 'rgba(255,255,255,0.35)',
              fontStyle: 'italic',
              lineHeight: 1.5,
            }}
          >
            "Linking people to resources not as ends in themselves, but as Ayat — signs pointing back to the One."
          </p>

          {/* CTA buttons */}
          <div className="reveal-on-scroll stagger-4 flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              className="group inline-flex items-center gap-3 font-body text-sm font-semibold text-white px-10 py-4 rounded-btn transition-all duration-300"
              style={{
                background: 'rgba(139,105,20,0.9)',
                border: '1px solid rgba(139,105,20,0.5)',
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(179,138,42,0.95)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(139,105,20,0.9)'; }}
            >
              Begin the Journey
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="transition-transform duration-300 group-hover:translate-x-1">
                <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            <button
              className="font-body text-sm font-medium transition-colors duration-200 border-b pb-0.5"
              style={{
                color: 'rgba(255,255,255,0.45)',
                borderColor: 'rgba(255,255,255,0.15)',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.7)';
                (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.4)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.45)';
                (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.15)';
              }}
            >
              Explore the Muslim Resource Guide
            </button>
          </div>

          {/* Closing Arabic phrase */}
          <div className="reveal-on-scroll stagger-5 mt-16">
            <div
              className="inline-block px-6 py-3 rounded"
              style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.06)',
              }}
            >
              <p
                className="font-heading text-2xl mb-1"
                style={{ color: 'rgba(255,255,255,0.6)', fontStyle: 'italic' }}
              >
                في سبيل الله
              </p>
              <p
                className="font-mono text-xs tracking-widest"
                style={{ color: 'rgba(179,138,42,0.5)', fontFamily: 'var(--font-mono)' }}
              >
                In the Way of Allah
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}